import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { selectCmsFromBootstrap, selectConfigFromBootstrap, selectFeatureBehaviorFromBootstrap, selectTopDestinationFromBootstrap } from 'store/slice/bootstrap';
import { TopDestination } from 'core/type/ActivitiesTopDestinations.types';
import { BootstrapConfig, CmsValue } from 'core/type/BootstrapConfig.types';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesEmptyStatePopularDestinationsClickEventActivitiesFieldsV2,
    ActivitiesEmptyStateSuggestionContainerSeenEventActivitiesFieldsV1,
    ActivitiesEmptyStateTopActivitiesClickEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesEmptyStatePopularDestinationsClickEventActivitiesManifestV2,
    ActivitiesEmptyStateSuggestionContainerSeenEventActivitiesManifestV1,
    ActivitiesEmptyStateTopActivitiesClickEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { routeTo } from 'store/slice/navigation';
import { EmptyStateItemType, SuggestionItem } from 'core/type/SuggestionResult.types';
import { TextSearchType } from 'core/type/TextSearch.types';
import { getSearchPageFromCityIdAndActivityIdUrl } from 'core/utils/activityUrlGenerator';

interface StateProps {
    config: BootstrapConfig;
    topDestinations: TopDestination[];
    cms: CmsValue;
    screenSize: ScreenSize;
    isSearchBoxFeatureGroup: boolean;
    isSearchBoxDropdown: boolean;
    isActivityRedirectExperimentEnabled: boolean;
}

interface DispatchProps {
    onEmptyStateSuggestionSelect: (type: EmptyStateItemType, index: number, id: number, cityId: number, url: string, isActivityRedirectExperimentEnabled: boolean) => void;
    trackEmptyStateSuggestionsSeen: () => void;
}

export type EmptyStateSuggestionProps = StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectTopDestinationFromBootstrap,
    selectCmsFromBootstrap,
    selectScreenSize,
    selectFeatureBehaviorFromBootstrap,
    (config, topDestinations, cms, screenSize, featureBehaviors): StateProps => {
        const isSearchBoxFeatureGroup = featureBehaviors.SearchBoxFeatureGroup;
        return {
            config,
            topDestinations,
            cms,
            screenSize,
            isSearchBoxFeatureGroup,
            isSearchBoxDropdown: featureBehaviors.SearchBoxPopOverFeature,
            isActivityRedirectExperimentEnabled: featureBehaviors.EmptyStateSuggestionsRedirect,
        };
    },
);

export const mapTopDestinationToSuggestionItem = (destination: TopDestination) => {
    return {
        id: destination.cityId,
        activityId: destination.activityId,
        activityName: destination.activityName,
        cityId: destination.cityId,
        cityName: destination.cityName,
        countryName: destination.countryName,
        url: destination.cityUrl,
        type: TextSearchType.City,
    } as SuggestionItem;
};

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    onEmptyStateSuggestionSelect: (type: EmptyStateItemType, index: number, id: number, cityId: number, url: string, isActivityRedirectExperimentEnabled: boolean) => {
        if (type === EmptyStateItemType.TopActivities) {
            trackElementEvent<ActivitiesEmptyStateTopActivitiesClickEventActivitiesFieldsV2>(ActivitiesEmptyStateTopActivitiesClickEventActivitiesManifestV2.ids, {
                action_element_index: index,
                activity_id: id,
            });
        } else if (type === EmptyStateItemType.PopularDestinations) {
            trackElementEvent<ActivitiesEmptyStatePopularDestinationsClickEventActivitiesFieldsV2>(ActivitiesEmptyStatePopularDestinationsClickEventActivitiesManifestV2.ids, {
                action_element_index: index,
                city_id: id,
            });
        }

        if (type === EmptyStateItemType.TopActivities && isActivityRedirectExperimentEnabled) {
            const searchPageUrl = getSearchPageFromCityIdAndActivityIdUrl(cityId, id);
            dispatch(routeTo(searchPageUrl));
        } else {
            dispatch(routeTo(url));
        }
    },

    trackEmptyStateSuggestionsSeen: () => {
        trackElementEvent<ActivitiesEmptyStateSuggestionContainerSeenEventActivitiesFieldsV1>(ActivitiesEmptyStateSuggestionContainerSeenEventActivitiesManifestV1.ids, {});
    },
});
